<template>
	<SlidingSheet
		:is-open="isOpen"
		title="Export System"
		@close="onClose"
	>
		<template #content>
			<div class="system-export-content">
				<div class="mb-4">
					<h3 class="text-h6 mb-2">
						{{ systemName }}
					</h3>
					<p class="text-body-2 text--secondary">
						Select the integrations you want to include in the export:
					</p>
				</div>

				<div class="mb-4">
					<v-checkbox
						v-model="selectAll"
						label="Select All Services"
						class="mt-0"
						@change="onSelectAllChange"
					/>
				</div>

				<div>
					<v-card
						v-for="service in integrationServices"
						:key="service.id"
						class="mb-3"
						outlined
					>
						<v-card-text class="py-3">
							<div class="d-flex align-center">
								<v-checkbox
									v-model="selectedServices"
									:value="service.id"
									:label="service.name"
									class="mt-0 pt-0"
									hide-details
								/>
							</div>
						</v-card-text>
					</v-card>
				</div>
			</div>
		</template>

		<template #footer>
			<PrimaryActionButton
				@click="onExport"
			>
				Download
			</PrimaryActionButton>
		</template>
	</SlidingSheet>
</template>

<script>
import { SlidingSheet } from '../../../components/component-library-wrapper.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import downloadFileMixin from '../../../../../shared/mixins/download-file.js'
import PrimaryActionButton from '../../../../../shared/components/primary-action-button.vue'

export default {
	components: {
		SlidingSheet,
		PrimaryActionButton
	},
	mixins: [downloadFileMixin],
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		systemIntegration: {
			type: Object,
			default: null
		},
		integrationServices: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			selectedServices: [],
			selectAll: false
		}
	},
	computed: {
		systemName () {
			return this.systemIntegration ? this.systemIntegration.integrationSystemName : ''
		}
	},
	watch: {
		isOpen (newVal) {
			if (newVal) {
				this.selectedServices = []
				this.selectAll = false
			}
		},
		selectedServices (newVal) {
			this.selectAll = newVal.length === this.integrationServices.length
		}
	},
	methods: {
		onClose () {
			this.$emit('close')
		},
		onSelectAllChange (selectAll) {
			if (selectAll) {
				this.selectedServices = this.integrationServices.map(service => service.id)
			} else {
				this.selectedServices = []
			}
		},
		onExport () {
			// Generate system integration export data using existing onExportSystem logic
			const systemExportData = this.getSystemExportData()

			// Create the JSON structure as specified
			const exportData = {
				systemIntegration: systemExportData
			}

			// Only add integrationServices array if services are selected
			if (this.selectedServices.length > 0) {
				// Get selected services export data using existing onExportService logic
				const selectedServicesData = this.integrationServices
					.filter(service => this.selectedServices.includes(service.id))
					.map(service => this.getServiceExportData(service))

				exportData.integrationServices = selectedServicesData
			}

			// Create and download JSON file
			const fileName = `${this.systemName.replace(/[^a-zA-Z0-9]/g, '_')}_export.json`
			this.downloadJsonFile(exportData, fileName)

			// Show success message and close sheet
			showSnackbar(`System "${this.systemName}" downloaded successfully`)
			this.onClose()
		},
		getSystemExportData () {
			// This uses the same logic as onExportSystem in _connector-service.vue
			const systemIntegration = this.systemIntegration
			return {
				integrationSystemName: systemIntegration.integrationSystemName,
				endpointAddress: systemIntegration.endpointAddress,
				authenticationTypeId: systemIntegration.authenticationTypeId,
				enabled: false,
				brandId: systemIntegration.brandId,
				frequencyInSeconds: systemIntegration.frequencyInSeconds,
				auth2Scope: systemIntegration.auth2Scope,
				authenticationUrl: systemIntegration.authenticationUrl,
				notes: systemIntegration.notes,
				templateGUID: systemIntegration.templateGUID,
				jwtCertKey: systemIntegration.jwtCertKey || '',
				resource: systemIntegration.resource || '',
				validFrom: systemIntegration.validFrom,
				validTo: systemIntegration.validTo,
				clientId: '',
				clientSecret: '',
				username: '',
				password: '',
				token: ''
			}
		},
		getServiceExportData (integrationService) {
			// This uses the same logic as onExportService in _connector-service.vue
			return {
				name: integrationService.name,
				cassieObjectId: integrationService.cassieObjectId,
				isEnabled: integrationService.isEnabled,
				updateStandDak: integrationService.updateStandDak,
				brandId: integrationService.brandId,
				requestThrottlingWaitTimeMS: integrationService.requestThrottlingWaitTimeMS,
				requestUrl: integrationService.requestUrl,
				requestBody: this.parseJsonSafely(integrationService.requestBody),
				authenticationUrl: integrationService.authenticationUrl,
				httpMethod: integrationService.httpMethod,
				notes: integrationService.notes,
				customHeaders: integrationService.customHeaders,
				successResponses: integrationService.successResponses,
				filters: integrationService.filters,
				objectIdColumn: integrationService.objectIdColumn,
				objectClientUrnColumn: integrationService.objectClientUrnColumn,
				trigger: integrationService.trigger,
				objectFilterClause: integrationService.objectFilterClause,
				objectSelectList: Array.isArray(integrationService.objectSelectList)
					? integrationService.objectSelectList.join(',')
					: integrationService.objectSelectList || '',
				clientTargetObjectName: integrationService.clientTargetObjectName,
				targetSystemAction: integrationService.targetSystemAction,
				returnedValue: integrationService.returnedValue
			}
		},
		parseJsonSafely (jsonString) {
			try {
				if (!jsonString || typeof jsonString !== 'string') {
					return {}
				}
				return JSON.parse(jsonString)
			} catch (error) {
				console.warn('Failed to parse JSON, returning as string:', jsonString, error)
				return jsonString
			}
		}
	}
}
</script>

<style scoped>
.system-export-content {
	padding: 16px 0;
}
</style>
